import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  MenuItem,
  Select,
  InputLabel,
  Paper,
} from "@mui/material";
import { QEStrategydataFlowContext } from "../../../main-pages/QEStrategyMainPage";
import QEStrategyCommonButton from "../qe-strategy-homepage/CommonButton";
import { usePrefillForm } from "../../../../utils/qe-strategy-utils/usePreFillForm";

const BusinessContextAndRiskLandscape = ({ onNext }) => {
  const {
    businessContextAndRiskLandscape,
    setBusinessContextAndRiskLandscape,
    dataFromFileUploads,
  } = useContext(QEStrategydataFlowContext);

  const [formValues, setFormValues] = useState({
    businessFunction: "",
    businessOperation: "",
    strategicImportance: "",
    businessRisk: "",
    compliance: "",
    auditFrequency: "",
    userlevelTrust: "",
    businessLogicChange: "",
  });


  // Sync formValues with context data when context changes
  useEffect(() => {
    if (
      businessContextAndRiskLandscape &&
      Object.keys(businessContextAndRiskLandscape).length > 0
    ) {
      setFormValues((prevFormValues) => ({
        ...prevFormValues,
        ...businessContextAndRiskLandscape,
      }));
    }
  }, [businessContextAndRiskLandscape]);

  const handleChange = (field) => (event) => {
    setFormValues({ ...formValues, [field]: event.target.value });
  };

  const handleNext = () => {
    setBusinessContextAndRiskLandscape(formValues);
    onNext();
  };

  // dropdown state (instead of hardcoded MenuItem list)
  const [complianceOptions, setComplianceOptions] = useState([
    "GDPR",
    "SOX",
    "HIPAA",
    "ISO",
    "SLA",
    "PCI-DSS",
    "FedRAMP",
    "FISMA",
    "NIST",
    "CCPA/CPRA",
    "SOC 2 Type II",
    "Basel III / Solvency II",
    "None",
  ]);

  const questionToFieldMap = {
    "1. What business function does the system support?": "businessFunction",
    "2. Is the application core or supporting for business operations?":
      "businessOperation",
    "3. How would you rank the strategic importance of this application?":
      "strategicImportance",
    "4. What is the business risk if the system fails?": "businessRisk",
    "5. Are there compliance or regulatory requirements?": "compliance",
    "6. How frequently do regulatory audits occur?": "auditFrequency",
    "7. What is the required user trust level for this application?":
      "userlevelTrust",
    "8. Does the application frequently undergo business logic changes?":
      "businessLogicChange",
  };

  usePrefillForm(
    dataFromFileUploads,
    questionToFieldMap,
    formValues,
    setFormValues,
    {
      compliance: {
        options: complianceOptions,
        setOptions: setComplianceOptions,
      },
    }
  );
  return (
    <Box
      sx={{
        width: {
          xs: "100%", // full width on small screens
          sm: "100%",
          md: "1030px", // fixed width on medium and above
        },
        mx: "auto", // center horizontally
        mt: { xs: 5, sm: 8, md: 3 },
        mb: { xs: 4, md: 6 },
        px: { xs: 2, sm: 3 }, // side padding only for smaller screens
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
        }}
      >
        <Typography variant="h6" gutterBottom sx={{ fontSize: 20, position: 'relative', top: '-12px'}}>
          Business Context & Risk Landscape
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.02,
          }}
        >
          {/* Business Function */}
          <TextField
            fullWidth
            label="What business function does the system support"
            placeholder="Enter"
            value={formValues.businessFunction}
            onChange={handleChange("businessFunction")}
            margin="normal"
            sx={{
              height: "auto", // allow height to expand responsively
            }}
            slotProps={{
              inputLabel: {
                shrink: true,
              },
            }}
          />

          {/* Core / Supporting */}
          <FormControl margin="normal">
            <FormLabel>
              Is the application core or supporting for business operations?
            </FormLabel>
            <RadioGroup
              row
              value={formValues.businessOperation}
              onChange={handleChange("businessOperation")}
            >
              <FormControlLabel value="Core" control={<Radio />} label="Core" />
              <FormControlLabel
                value="Supporting"
                control={<Radio />}
                label="Supporting"
              />
            </RadioGroup>
          </FormControl>

          {/* Strategic Importance */}
          <FormControl margin="normal">
            <FormLabel>
              How would you rank the strategic importance of this application?
            </FormLabel>
            <RadioGroup
              row
              value={formValues.strategicImportance}
              onChange={handleChange("strategicImportance")}
            >
              <FormControlLabel value="High" control={<Radio />} label="High" />
              <FormControlLabel
                value="Medium"
                control={<Radio />}
                label="Medium"
              />
              <FormControlLabel value="Low" control={<Radio />} label="Low" />
            </RadioGroup>
          </FormControl>

          {/* Business Risk */}
          <FormControl margin="normal">
            <FormLabel>
              What is the business risk if the system fails?
            </FormLabel>
            <RadioGroup
              row
              value={formValues.businessRisk}
              onChange={handleChange("businessRisk")}
            >
              <FormControlLabel
                value="Critical"
                control={<Radio />}
                label="Critical"
              />
              <FormControlLabel
                value="Major"
                control={<Radio />}
                label="Major"
              />
              <FormControlLabel
                value="Minor"
                control={<Radio />}
                label="Minor"
              />
            </RadioGroup>
          </FormControl>

          {/* Compliance */}
          <FormControl fullWidth margin="normal" variant="outlined">
  <InputLabel
    shrink
    id="compliance-label"
    sx={{
      background: "#fff",      
      px: 0.5,                  
      mb: 0.5,                  
    }}
  >
    Are there compliance or regulatory requirements?
  </InputLabel>
  <Select
    labelId="compliance-label"
    value={formValues.compliance}
    onChange={handleChange("compliance")}
    displayEmpty
    renderValue={(selected) => {
      if (!selected) {
        return <span style={{ color: "#aaa" }}>Select</span>;
      }
      return selected;
    }}
    MenuProps={{
      PaperProps: {
        style: {
          maxHeight: 200,
          width: 300,
        },
      },
    }}
  >
    {complianceOptions.map((option) => (
      <MenuItem key={option} value={option}>
        {option}
      </MenuItem>
    ))}
  </Select>
</FormControl>
 
 







          {/* Audit Frequency */}
          <FormControl margin="normal">
            <FormLabel>How frequently do regulatory audits occur?</FormLabel>
            <RadioGroup
              row
              value={formValues.auditFrequency}
              onChange={handleChange("auditFrequency")}
            >
              <FormControlLabel
                value="Never"
                control={<Radio />}
                label="Never"
              />
              <FormControlLabel
                value="Annually"
                control={<Radio />}
                label="Annually"
              />
              <FormControlLabel
                value="Quarterly"
                control={<Radio />}
                label="Quarterly"
              />
            </RadioGroup>
          </FormControl>

          {/* Trust Level */}
          <FormControl margin="normal">
            <FormLabel>
              What is the required user trust level for this application?
            </FormLabel>
            <RadioGroup
              row
              value={formValues.userlevelTrust}
              onChange={handleChange("userlevelTrust")}
            >
              <FormControlLabel value="High" control={<Radio />} label="High" />
              <FormControlLabel
                value="Moderately"
                control={<Radio />}
                label="Moderately"
              />
              <FormControlLabel value="Low" control={<Radio />} label="Low" />
            </RadioGroup>
          </FormControl>

          {/* Business Logic Change */}
          <FormControl margin="normal">
            <FormLabel>
              Does the application frequently undergo business logic changes?
            </FormLabel>
            <RadioGroup
              row
              value={formValues.businessLogicChange}
              onChange={handleChange("businessLogicChange")}
            >
              <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
              <FormControlLabel value="No" control={<Radio />} label="No" />
            </RadioGroup>
          </FormControl>
        </Box>
      </Paper>

      {/* Next button OUTSIDE the Paper */}
      <QEStrategyCommonButton
        showBack={false}
        showNext={true}
        handleNext={handleNext}
      />
    </Box>
  );
};

export default BusinessContextAndRiskLandscape;
